import type {
  GreenCoffee,
  GreenCoffeeGrade,
  GreenCoffeeJudge,
  GreenCoffeeListItem,
} from '@/hooks/useGreenCoffee';
import { relTime } from '@/lib/crawlRuns';

import { BagTile } from './BagTile';
import { CheckIcon, ChevronRightIcon, CrossIcon, PencilIcon, UpdateIcon } from './greenCoffeeIcons';
import { correctedNote, isCorrected, isJudgePublished } from './greenCoffeeStatus';

export function KindBadge({ green }: { green: GreenCoffeeListItem }) {
  const label = green.isBlend ? 'Blend' : 'Single origin';
  return (
    <span className="flex flex-wrap justify-end gap-1">
      <span
        className={`inline-flex items-center rounded-md px-[7px] py-[3px] text-[10.5px] font-bold uppercase tracking-[0.03em] ${
          green.isBlend
            ? 'bg-wolisho-brown/[0.09] text-wolisho-brown'
            : 'bg-green-600/10 text-green-700'
        }`}
      >
        {label}
      </span>
      {green.isUpdate && (
        <span className="inline-flex items-center gap-1 rounded-md bg-wolisho-brown/[0.09] px-[7px] py-[3px] text-[10.5px] font-bold uppercase tracking-[0.03em] text-wolisho-brown">
          <UpdateIcon />
          Update
        </span>
      )}
    </span>
  );
}

export function GradePill({ grade }: { grade: GreenCoffeeGrade }) {
  const good = grade === 'good';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-[7px] py-[3px] text-[10.5px] font-bold ${
        good ? 'bg-green-600/10 text-green-700' : 'bg-wolisho-red/10 text-wolisho-red'
      }`}
    >
      {good ? <CheckIcon size={10} /> : <CrossIcon size={10} />}
      {good ? 'Good · published' : 'Bad'}
    </span>
  );
}

// A row the judge published, with no human grade. Neutral brown, so it reads
// apart from a human Good.
export function JudgePublishedPill() {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-md bg-wolisho-brown/[0.09] px-[7px] py-[3px] text-[10.5px] font-bold text-wolisho-brown"
      data-testid="green-coffee-judge-published-pill"
    >
      <CheckIcon size={10} />
      Judge · published
    </span>
  );
}

export function CorrectedPill() {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-md bg-green-600/10 px-[7px] py-[3px] text-[10.5px] font-bold text-green-700"
      data-testid="green-coffee-corrected-pill"
    >
      <PencilIcon size={10} />
      Corrected · published
    </span>
  );
}

// Who put a row where it is: the human grade when there is one, else the judge.
export function StatusPill({ green }: { green: GreenCoffee }) {
  if (isCorrected(green)) return <CorrectedPill />;
  if (green.grade) return <GradePill grade={green.grade} />;
  if (isJudgePublished(green)) return <JudgePublishedPill />;
  return null;
}

// The judge's verdict on a row still in the queue (or graded over it). A PASS
// that has not published yet only shows while its publish retries.
export function JudgeVerdictPill({ judge }: { judge: GreenCoffeeJudge }) {
  const fail = judge.verdict === 'FAIL';
  return (
    <span
      className={`mt-1.5 inline-flex items-center gap-1 rounded-md px-[7px] py-[3px] text-[10.5px] font-bold ${
        fail ? 'bg-wolisho-red/10 text-wolisho-red' : 'bg-wolisho-brown/[0.09] text-wolisho-brown'
      }`}
      data-testid="green-coffee-judge-pill"
    >
      {fail ? <CrossIcon size={10} /> : <CheckIcon size={10} />}
      {fail ? `Judge: FAIL · rule ${String(judge.rule)}` : 'Judge: PASS · publishing'}
    </span>
  );
}

function timestamp(green: GreenCoffee): string {
  if (isCorrected(green)) return `corrected ${relTime(green.gradedAt ?? green.createdAt)}`;
  if (green.grade) return `graded ${relTime(green.gradedAt ?? green.createdAt)}`;
  if (isJudgePublished(green)) {
    return `by the judge ${relTime(green.judge?.judgedAt ?? green.createdAt)}`;
  }
  return `queued ${relTime(green.createdAt)}`;
}

interface Props {
  green: GreenCoffeeListItem;
  roasterName: string | null;
  onOpen: (id: string) => void;
}

export function GreenCoffeeQueueRow({ green, roasterName, onOpen }: Props) {
  const judgePublished = isJudgePublished(green);
  // A judge-published or corrected row says so in its status pill; anywhere
  // else the verdict sits under the name.
  const showVerdict = green.judge !== null && !judgePublished && !isCorrected(green);
  const note = correctedNote(green) ?? (green.grade ? green.notes : null);
  return (
    <button
      type="button"
      onClick={() => onOpen(green.id)}
      data-testid={`green-coffee-row-${green.id}`}
      className="flex w-full items-center gap-2.5 border-0 bg-transparent px-3.5 py-3 text-left"
    >
      <BagTile label={roasterName ?? green.fields.name} size={40} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-black">{green.fields.name}</p>
        <p className="mt-0.5 truncate text-xs font-bold text-black/45">
          {roasterName ? `${roasterName} · ` : ''}
          {timestamp(green)}
        </p>
        {showVerdict && green.judge && <JudgeVerdictPill judge={green.judge} />}
        {note && (
          <p className="mt-1 line-clamp-2 text-xs italic leading-snug text-black/50">
            &ldquo;{note}&rdquo;
          </p>
        )}
      </div>
      <span className="flex shrink-0 flex-col items-end gap-1.5">
        {green.grade || judgePublished ? <StatusPill green={green} /> : <KindBadge green={green} />}
        <span className="flex text-wolisho-brown/30">
          <ChevronRightIcon />
        </span>
      </span>
    </button>
  );
}
